import React from 'react';
import { FormattedMessage } from 'react-intl';
import classNames, { Argument } from 'classnames';

import Icon from './Icon';

type ExpandableTextAreaProps = {
  data: React.ReactNode[],
  className?: Argument,
  rowSize?: number,
};

export const DEFAULT_ROW_SIZE = 6;

const ExpandableList: React.FC<ExpandableTextAreaProps> = ({ data, className, rowSize = DEFAULT_ROW_SIZE }) => {
  const [nbRowDisplayed, setNbRowDisplayed] = React.useState<number>(rowSize);

  const isCollapsed = React.useMemo(() => nbRowDisplayed < data.length, [data, nbRowDisplayed]);
  const displayedData = React.useMemo(() => data.filter((_, index) => index < nbRowDisplayed), [data, nbRowDisplayed]);

  const handleClick = React.useCallback(() => {
    setNbRowDisplayed(isCollapsed ? data.length : rowSize);
  }, [setNbRowDisplayed, data, isCollapsed, rowSize]);

  return (
    <div
      className={classNames('flex flex-col bg-card-bg rounded-card p-4 kiosk:p-6 w-full', className)}
    >
      {displayedData.map((item, index) => (
        <React.Fragment key={index}>{item}</React.Fragment>
      ))}
      {data.length > rowSize && (
        <div
          className="w-full pt-3 cursor-pointer"
          onClick={handleClick}
          data-testid="ExpandCollapseBtn"
        >
          <div className="flex justify-center items-center kiosk:text-2xl kioskSharebox:text-xl">
            {isCollapsed && <FormattedMessage id="list.viewAll" defaultMessage="View all" />}
            {!isCollapsed && <FormattedMessage id="list.reduce" defaultMessage="Reduce" />}
            <Icon className={classNames(
              'text-xl kiosk:text-3xl text-default ml-3',
              isCollapsed ? 'fb-icon--arrow-down' : 'fb-icon--arrow-up',
            )}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableList;
